import { Link } from 'react-router-dom';
import { useIsMobile } from '@/hooks/useIsMobile';
import { formatCurrencyToMajor } from '@/utils/formatCurrency';
import AuthorImage from '@/components/elements/AuthorImage';
import SubscriptionStatus from '@/components/elements/SubscriptionStatus';
import SubscriptionPaymentDate from '@/components/elements/SubscriptionPaymentDate';
import CancelSubscription from '@/components/settings/CancelSubscription';
import type { Subscription } from '@/types/models.types';

function SubscriptionCard({ subscription }: { subscription: Subscription }) {
  const isMobile = useIsMobile();
  const { id, product, status, trialStatus, lastPayment, periodEnd, author, stripeSubscriptionId } = subscription;
  const isPackageSubscription = author === null || stripeSubscriptionId === null;

  return (
    <div data-cy='subscription-card' key={id} className='bg-white rounded-md border border-gray-200'>
      <header className='md:flex flex-col md:flex-row md:items-center justify-between p-4 bg-gray-100 rounded-t-md gap-4 md:gap-0 h-[74px]'>
        <div className='flex items-center gap-3'>
          <AuthorImage
            src={author?.image}
            alt={`${author?.name}'s profile picture`}
            placeholder={isPackageSubscription ? 'eklipX' : undefined}
          />

          <div>
            <h3 className='!text-base !leading-5 font-semibold m-0 2xl:text-xl'>
              {author?.name ? (
                <Link to={`/author/${author.lookupKey}`} className='transition-all hover:text-teal-700 hover:underline'>
                  {author.name}
                </Link>
              ) : (
                product.description
              )}
            </h3>

            <SubscriptionStatus status={status} trialStatus={trialStatus} />
          </div>
        </div>

        {!isMobile && <CancelSubscription subscription={subscription} />}
      </header>

      <div className='p-4 py-3 flex flex-col md:flex-row justify-between md:items-center'>
        <SubscriptionPaymentDate
          status={status}
          lastPayment={lastPayment}
          className='mt-1 md:mt-0'
          periodEnd={periodEnd}
          stripeSubscriptionId={stripeSubscriptionId}
        />

        {!isPackageSubscription && (
          <p className='font-semibold text-[#002327]'>
            {formatCurrencyToMajor(product.price, 'gbp')}
            <span className='text-sm -mt-[6px] font-normal text-slate-500 w-max ml-[4px]'>per month</span>
          </p>
        )}

        {isMobile && <CancelSubscription className='mt-3 mb-1' subscription={subscription} />}
      </div>
    </div>
  );
}

export default SubscriptionCard;
